import { useMemo } from 'react';
import { useUser } from '@descope/react-sdk';
import { useParams } from 'react-router-dom';
import { getAuthContextFromDescope } from '@/lib/descope-roles';
import { OrganizationRole, roleGroups } from '@/lib/roles';

/**
 * Role-based authorization configuration
 * Matches backend RolesConfig interface
 */
export interface RolesConfig {
  /** Route parameter containing the organization ID (must be 'organizationId' if specified) */
  param?: 'organizationId';
  /** User must have ANY of these roles */
  anyOf?: string[];
  /** User must have ALL of these roles */
  allOf?: string[];
}

/**
 * Hook to check if the current user has required roles
 *
 * Reads roles directly from the Descope access token (via useUser hook).
 * No additional API calls needed - roles are already in the token!
 *
 * @param rolesConfig - Role requirements configuration
 * @param organizationId - Optional organization ID (can be passed directly or read from route params)
 * @returns Object with hasAccess boolean and loading state
 *
 * @example
 * // Global role check
 * const { hasAccess, isLoading } = useRoleCheck({ anyOf: ['user'] });
 *
 * @example
 * // Organization role check using route params
 * const { hasAccess } = useRoleCheck({
 *   param: 'organizationId',
 *   anyOf: ['owner', 'manager']
 * });
 *
 * @example
 * // Organization role check with explicit org ID
 * const { hasAccess } = useRoleCheck(
 *   { anyOf: ['owner'] },
 *   'T34KuvtaSwI4vuGHEU6uCfPTHccZ'
 * );
 */
export function useRoleCheck(
  rolesConfig?: RolesConfig,
  explicitOrganizationId?: string
) {
  const { user, isUserLoading } = useUser();
  const params = useParams<{ organizationId?: string }>();

  const result = useMemo(() => {
    // If no roles config, allow access (authenticated users only need to be authenticated)
    if (!rolesConfig) {
      return { hasAccess: true, isLoading: isUserLoading };
    }

    // Wait for user data to load
    if (isUserLoading || !user) {
      return { hasAccess: false, isLoading: isUserLoading };
    }

    // Transform Descope user data (cached by useMemo)
    const authContext = getAuthContextFromDescope(user);

    // Organization-scoped authorization
    if (rolesConfig.param) {
      const organizationId =
        explicitOrganizationId || params[rolesConfig.param];

      if (!organizationId) {
        return { hasAccess: false, isLoading: false };
      }

      // Check if user is a member of this organization (org ID === tenant ID)
      const tenantPermissions = authContext.tenants[organizationId];

      if (!tenantPermissions) {
        // User is not a member
        return { hasAccess: false, isLoading: false };
      }

      // Check role requirements
      const userRoles = tenantPermissions.roles || [];

      if (rolesConfig.anyOf) {
        const hasAnyRole = rolesConfig.anyOf.some(role =>
          userRoles.includes(role)
        );
        if (!hasAnyRole) {
          return { hasAccess: false, isLoading: false };
        }
      }

      if (rolesConfig.allOf) {
        const hasAllRoles = rolesConfig.allOf.every(role =>
          userRoles.includes(role)
        );
        if (!hasAllRoles) {
          return { hasAccess: false, isLoading: false };
        }
      }

      return { hasAccess: true, isLoading: false };
    }
    // Global role authorization
    else {
      const userRoles = authContext.globalRoles || [];

      if (rolesConfig.anyOf) {
        const hasAnyRole = rolesConfig.anyOf.some(role =>
          userRoles.includes(role)
        );
        if (!hasAnyRole) {
          return { hasAccess: false, isLoading: false };
        }
      }

      if (rolesConfig.allOf) {
        const hasAllRoles = rolesConfig.allOf.every(role =>
          userRoles.includes(role)
        );
        if (!hasAllRoles) {
          return { hasAccess: false, isLoading: false };
        }
      }

      return { hasAccess: true, isLoading: false };
    }
  }, [rolesConfig, user, isUserLoading, params, explicitOrganizationId]);

  return result;
}

/**
 * Result type for useIsOwnerOrManager hook
 */
export interface UseIsOwnerOrManagerResult {
  /** Whether user has owner or manager role */
  isOwnerOrManager: boolean;
  /** Whether user data is still loading */
  isLoading: boolean;
}

/**
 * Hook to check if user has owner or manager role in an organization.
 * Convenience wrapper around useRoleCheck for the common owner/manager pattern.
 *
 * @param organizationId - Organization ID to check roles for (optional for dashboard context)
 * @returns Object with isOwnerOrManager boolean and loading state
 *
 * @example
 * // In organization context
 * const { isOwnerOrManager, isLoading } = useIsOwnerOrManager(organizationId);
 *
 * @example
 * // Ignoring loading state when parent handles it
 * const { isOwnerOrManager } = useIsOwnerOrManager(organizationId);
 */
export function useIsOwnerOrManager(
  organizationId: string | undefined
): UseIsOwnerOrManagerResult {
  const { hasAccess, isLoading } = useRoleCheck(
    organizationId
      ? {
          param: 'organizationId',
          anyOf: [...roleGroups.managerOrOwner],
        }
      : undefined,
    organizationId
  );

  return { isOwnerOrManager: hasAccess, isLoading };
}

/** Result of {@link useCanViewAdminContent}. */
export interface UseCanViewAdminContentResult {
  /** Owner, manager, or support — can see org-wide (admin) data views. */
  canViewAdminContent: boolean;
  isLoading: boolean;
}

/**
 * Read-scope check: can this user see org-wide data (the admin view), as
 * opposed to the member self-view? True for owner, manager, and support.
 * Mirrors backend `roleGroups.managerOwnerOrSupport`.
 */
export function useCanViewAdminContent(
  organizationId: string | undefined
): UseCanViewAdminContentResult {
  const { hasAccess, isLoading } = useRoleCheck(
    organizationId
      ? {
          param: 'organizationId',
          anyOf: [...roleGroups.managerOwnerOrSupport],
        }
      : undefined,
    organizationId
  );
  return { canViewAdminContent: hasAccess, isLoading };
}

/** Result of {@link useIsSupport}. */
export interface UseIsSupportResult {
  isSupport: boolean;
  isLoading: boolean;
}

/**
 * True when the user holds the support role in this organization. Used to
 * render write controls as disabled (support is read-only).
 */
export function useIsSupport(
  organizationId: string | undefined
): UseIsSupportResult {
  const { hasAccess, isLoading } = useRoleCheck(
    organizationId
      ? { param: 'organizationId', anyOf: [OrganizationRole.Support] }
      : undefined,
    organizationId
  );
  return { isSupport: hasAccess, isLoading };
}

/** Result of {@link useOrgWriteGate}. */
export interface OrgWriteGate {
  /** Caller should render nothing (member in org scope). */
  hidden: boolean;
  /** Caller should render the control but disabled (support in org scope). */
  disabled: boolean;
  isLoading: boolean;
}

/**
 * Org-scoped write gate for mutate controls. Resolves a role into one of three
 * presentations:
 * - admin (owner/manager) → enabled (`hidden:false, disabled:false`)
 * - support → disabled (`hidden:false, disabled:true`)
 * - member → hidden (`hidden:true`)
 * Non-org scope (no organizationId) is never gated here (`hidden:false,
 * disabled:false`) — account-scope controls remain enabled.
 */
export function useOrgWriteGate(
  organizationId: string | undefined
): OrgWriteGate {
  const admin = useRoleCheck(
    organizationId
      ? { param: 'organizationId', anyOf: [...roleGroups.managerOrOwner] }
      : undefined,
    organizationId
  );
  const support = useIsSupport(organizationId);

  if (!organizationId) {
    return { hidden: false, disabled: false, isLoading: false };
  }
  if (admin.isLoading || support.isLoading) {
    return { hidden: false, disabled: false, isLoading: true };
  }
  if (admin.hasAccess) {
    return { hidden: false, disabled: false, isLoading: false };
  }
  if (support.isSupport) {
    return { hidden: false, disabled: true, isLoading: false };
  }
  return { hidden: true, disabled: false, isLoading: false };
}
